/**
 * Component to display sync jobs for a user with auto-refresh
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  InfoOutlined as InfoOutlinedIcon,
  Stop as StopIcon,
  PlayArrow as PlayArrowIcon,
  Sync as SyncIcon,
  PersonRemove as PersonRemoveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userApi } from '@/api/user';
import { jobApi, type Job } from '@/api/job';
import { useAuth } from '@/hooks/useAuth';

interface UserSyncJobsListProps {
  userUuid?: string;
  isConnected?: boolean;
  hasUnipileId?: boolean;
  canManageActions?: boolean;
  onCreateJob?: (fromDate: string, toDate: string) => void;
  onWithdrawPendingInvites?: () => void;
}

export const UserSyncJobsList: React.FC<UserSyncJobsListProps> = ({ 
  userUuid, 
  isConnected = false,
  hasUnipileId = false,
  canManageActions = true,
  onCreateJob,
  onWithdrawPendingInvites,
}) => {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const [autoRefresh, setAutoRefresh] = useState(false);
  const hasSetInitialDefault = useRef(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dateErrors, setDateErrors] = useState<{ fromDate?: string; toDate?: string }>({});

  // Use current user's key if userUuid not provided
  const targetUserUuid = userUuid || currentUser?.key;

  const { data: jobsData, isLoading, error } = useQuery({
    queryKey: ['user-sync-jobs', currentUser?.key, targetUserUuid],
    queryFn: () => userApi.getUserSyncJobs(targetUserUuid),
    enabled: !!targetUserUuid,
    refetchInterval: autoRefresh ? 5000 : false, // Refresh every 5 seconds when enabled
  });

  const updateJobMutation = useMutation({
    mutationFn: ({ jobUuid, data }: { jobUuid: string; data: Parameters<typeof jobApi.updateJob>[1] }) =>
      jobApi.updateJob(jobUuid, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-sync-jobs', currentUser?.key, targetUserUuid] });
    },
  });

  // Set default auto-refresh state only once when jobs data is first loaded
  useEffect(() => {
    if (jobsData?.output.items && !hasSetInitialDefault.current) {
      const hasRunningJobs = jobsData.output.items.some(
        (job) => job.status === 'IN_PROGRESS' || job.status === 'TODO'
      );
      if (hasRunningJobs) {
        setAutoRefresh(true);
      }
      hasSetInitialDefault.current = true;
    }
  }, [jobsData]);

  const handleStop = (jobUuid: string) => {
    updateJobMutation.mutate({ jobUuid, data: { status: 'STOP' } });
  };

  const handleResume = (jobUuid: string) => {
    updateJobMutation.mutate({ jobUuid, data: { status: 'TODO' } });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DONE':
        return 'success';
      case 'IN_PROGRESS':
        return 'info';
      case 'FAILED':
        return 'error';
      case 'STOP':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleOpenDetails = (job: Job) => {
    setSelectedJob(job);
    setIsDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setIsDetailsOpen(false);
    setSelectedJob(null);
  };

  const handleOpenCreateDialog = () => {
    // Set default dates: from 30 days ago to now
    const now = new Date();
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    setFromDate(thirtyDaysAgo.toISOString().slice(0, 16));
    setToDate(now.toISOString().slice(0, 16));
    setDateErrors({});
    setIsCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false);
    setFromDate('');
    setToDate('');
    setDateErrors({});
  };

  const validateDates = (): boolean => {
    const errors: { fromDate?: string; toDate?: string } = {};
    
    if (!fromDate) {
      errors.fromDate = 'Start date is required';
    }
    
    if (!toDate) {
      errors.toDate = 'End date is required';
    }
    
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      
      if (from >= to) {
        errors.toDate = 'End date must be after start date';
      }
    }
    
    setDateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateJob = () => {
    if (!validateDates()) {
      return;
    }
    
    if (onCreateJob) {
      onCreateJob(fromDate, toDate);
      handleCloseCreateDialog();
    }
  };

  const canCreateJob = isConnected && hasUnipileId && canManageActions;
  const canWithdrawInvites = isConnected && hasUnipileId && canManageActions;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load sync jobs. Please try again.</Alert>
      </Box>
    );
  }

  const jobs = jobsData?.output.items || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Actions</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {onCreateJob && (
            <Tooltip 
              title={
                !canManageActions
                  ? "You do not have access to run actions for this user"
                  : !hasUnipileId 
                    ? "User must have a Unipile account connected" 
                    : !isConnected 
                      ? "User's LinkedIn account is not connected" 
                      : "Sync messages for this user"
              }
            >
              <span>
                <Button
                  variant="contained"
                  startIcon={<SyncIcon />}
                  onClick={handleOpenCreateDialog}
                  size="small"
                  disabled={!canCreateJob}
                >
                  Sync Messages
                </Button>
              </span>
            </Tooltip>
          )}
          {onWithdrawPendingInvites && (
            <Tooltip 
              title={
                !canManageActions
                  ? "You do not have access to run actions for this user"
                  : !hasUnipileId 
                    ? "User must have a Unipile account connected" 
                    : !isConnected 
                      ? "User's LinkedIn account is not connected" 
                      : "Withdraw pending invites for this user"
              }
            >
              <span>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<PersonRemoveIcon />}
                  onClick={() => onWithdrawPendingInvites()}
                  size="small"
                  disabled={!canWithdrawInvites}
                >
                  Withdraw pending invites
                </Button>
              </span>
            </Tooltip>
          )}
          <FormControlLabel
            control={
              <Switch checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            }
            label="Auto-refresh"
          />
        </Box>
      </Box>

      {!canCreateJob && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {!canManageActions
            ? "You do not have access to run actions for this user."
            : !hasUnipileId 
              ? "User must have a Unipile account connected to run actions." 
              : "User's LinkedIn account is not connected. Please ensure the account is properly connected."}
        </Alert>
      )}

      {jobs.length === 0 ? (
        <Alert severity="info">No action jobs found.</Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {jobs.map((job) => {
            // Normalize progress: if > 1, it's already a percentage (0-100), otherwise it's a decimal (0-1)
            const progressValue = job.progress !== null && job.progress !== undefined ? job.progress : 0;
            const progress = progressValue > 1 ? Math.round(progressValue) : Math.round(progressValue * 100);
            const fromDate = job.payload?.from;
            const toDate = job.payload?.to;

            return (
              <Card key={job.key}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        {job.queue}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        <Chip label={job.status} color={getStatusColor(job.status) as any} size="small" />
                        {job.started_at && (
                          <Chip
                            icon={<ScheduleIcon />}
                            label={`Started: ${formatDate(job.started_at)}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {job.available_at && !job.started_at && (
                          <Chip
                            icon={<ScheduleIcon />}
                            label={`Scheduled: ${formatDate(job.available_at)}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {job.created_at && (
                          <Chip
                            label={`Created: ${formatDate(job.created_at)}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      {fromDate && toDate && (
                        <Typography variant="body2" color="text.secondary">
                          Date range: {formatDate(fromDate)} - {formatDate(toDate)}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {/* Stop button - show for IN_PROGRESS or TODO jobs */}
                      {(job.status === 'IN_PROGRESS' || job.status === 'TODO') && (
                        <Tooltip title="Stop job">
                          <IconButton
                            size="small"
                            onClick={() => handleStop(job.key)}
                            disabled={updateJobMutation.isPending}
                            color="error"
                          >
                            <StopIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Resume button - show for STOP jobs */}
                      {job.status === 'STOP' && (
                        <Tooltip title="Resume job">
                          <IconButton
                            size="small"
                            onClick={() => handleResume(job.key)}
                            disabled={updateJobMutation.isPending}
                            color="primary"
                          >
                            <PlayArrowIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip title="View job details">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDetails(job)}
                        >
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {job.status === 'IN_PROGRESS' && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Progress</Typography>
                        <Typography variant="body2">{progress}%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={progress} />
                    </Box>
                  )}

                  {job.failed_reason && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {job.failed_reason}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Job Details Dialog */}
      <Dialog open={isDetailsOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Job Details</Typography>
            <IconButton onClick={handleCloseDetails} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedJob && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Chip label={selectedJob.status} color={getStatusColor(selectedJob.status) as any} size="small" />
              </Box>
              {selectedJob.progress !== null && selectedJob.progress !== undefined && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Progress
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={selectedJob.progress > 1 ? selectedJob.progress : selectedJob.progress * 100}
                      sx={{ flexGrow: 1 }}
                    />
                    <Typography variant="body2">
                      {Math.round(selectedJob.progress > 1 ? selectedJob.progress : selectedJob.progress * 100)}%
                    </Typography>
                  </Box>
                </Box>
              )}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body2">{formatDate(selectedJob.created_at)}</Typography>
              </Box>
              {selectedJob.started_at && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Started
                  </Typography>
                  <Typography variant="body2">{formatDate(selectedJob.started_at)}</Typography>
                </Box>
              )}
              {selectedJob.available_at && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Scheduled
                  </Typography>
                  <Typography variant="body2">{formatDate(selectedJob.available_at)}</Typography>
                </Box>
              )}
              {selectedJob.payload?.from && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    From Date
                  </Typography>
                  <Typography variant="body2">{formatDate(selectedJob.payload.from)}</Typography>
                </Box>
              )}
              {selectedJob.payload?.to && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    To Date
                  </Typography>
                  <Typography variant="body2">{formatDate(selectedJob.payload.to)}</Typography>
                </Box>
              )}
              {selectedJob.failed_reason && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Error
                  </Typography>
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {selectedJob.failed_reason}
                  </Alert>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create Sync Job Dialog */}
      <Dialog open={isCreateDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Create Sync Job</Typography>
            <IconButton onClick={handleCloseCreateDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <TextField
              label="Start Date"
              type="datetime-local"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                if (dateErrors.fromDate) {
                  setDateErrors(prev => ({ ...prev, fromDate: undefined }));
                }
              }}
              error={!!dateErrors.fromDate}
              helperText={dateErrors.fromDate || "Select the start date for syncing messages"}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              label="End Date"
              type="datetime-local"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                if (dateErrors.toDate) {
                  setDateErrors(prev => ({ ...prev, toDate: undefined }));
                }
              }}
              error={!!dateErrors.toDate}
              helperText={dateErrors.toDate || "Select the end date for syncing messages"}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateJob}
            startIcon={<SyncIcon />}
          >
            Create Sync Job
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
